import React, { useState, useContext } from "react";
import { Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";

import { getToken } from "../../../../api/Account";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { useAviso } from "../../../../components/base";
import { formatearFecha, formatearMonto } from "../../../../helpers/formato";
import { deletePrestamoAsync, getPrestamosByEmployeeAsync } from "../../../../api/Employees";
import AddPrestamo from "../prestamos/AddPrestamo";
import AddAbono from "../prestamos/AddAbono";
import UpdatePrestamo, { EstadoPrestamo } from "../prestamos/UpdatePrestamo";
import HistorialTab from "./HistorialTab";
import {
  celdaFecha,
  celdaMonto,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

const FILAS_POR_PAGINA = 10;

const saldoDe = (row) => row.monto - row.totalAbonado;

const Prestamos = ({ id }) => {
  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const [selectedPrestamo, setSelectedPrestamo] = useState(null);
  const [abonando, setAbonando] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);

  const {
    datos: histoyPrestamos,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getPrestamosByEmployeeAsync(token, id));

  const deletePrestamo = async (prestamoId) => {
    const ok = await escribir(
      () => deletePrestamoAsync(token, prestamoId),
      "No se pudo eliminar el préstamo"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Préstamo eliminado");
  };

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar préstamo",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminará el préstamo #${row.id}${
              row.descripcion ? ` (${row.descripcion})` : ""
            } de ${formatearMonto(row.monto)} del ${formatearFecha(row.fechaCreacion)}.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar préstamo",
      peligro: true,
      onConfirmar: () => deletePrestamo(row.id),
    });

  // Cada botón pide el permiso del endpoint que llama (EmployeesController): agregar y abonar,
  // NOMINA CREAR; eliminar y anular, NOMINA UPDATE (más NOMINA DELETE en la pantalla). Agregar además carga los plazos, que el
  // servidor reserva a CLIENTS VER: sin él daría 401 y la web cerraría la sesión.
  const puedeAbonar = tienePermiso(access, "NOMINA CREAR");
  const puedeAgregar = puedeAbonar && tienePermiso(access, "CLIENTS VER");
  // Como en Adelantos y Bonos: la pantalla pedía NOMINA DELETE y el servidor exige NOMINA UPDATE.
  const puedeEliminar =
    tienePermiso(access, "NOMINA DELETE") && tienePermiso(access, "NOMINA UPDATE");
  // Sin CLIENTS VER no se cargan los plazos y el alta no se puede ofrecer.
  const faltaVerClientes = puedeAbonar && !tienePermiso(access, "CLIENTS VER");

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "sm",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "fecha",
      etiqueta: "Fecha",
      sx: celdaFecha,
      render: (row) => formatearFecha(row.fechaCreacion),
    },
    {
      id: "descripcion",
      etiqueta: "Descripción",
      desde: "md",
      render: (row) => row.descripcion || "—",
    },
    {
      id: "monto",
      etiqueta: "Monto",
      alinear: "right",
      sx: celdaMonto,
      render: (row) => (
        <Typography variant="monto" component="span">
          {formatearMonto(row.monto)}
        </Typography>
      ),
    },
    {
      id: "saldo",
      etiqueta: "Saldo",
      alinear: "right",
      sx: celdaMonto,
      render: (row) => (
        <Typography variant="monto" component="span">
          {formatearMonto(saldoDe(row))}
        </Typography>
      ),
    },
    {
      id: "estado",
      etiqueta: "Estado",
      desde: "sm",
      render: (row) => <EstadoPrestamo cancelado={row.isCanceled} />,
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Préstamos"
        descripcion={
          <>
            Préstamos al empleado: la cuota se descuenta en cada nómina.
            {faltaVerClientes ? (
              <Typography component="span" variant="body2" sx={{ display: "block", mt: 0.5 }}>
                Para agregar préstamos se necesita además el permiso Ver clientes (para los plazos).
              </Typography>
            ) : null}
          </>
        }
        etiquetaTabla="Historial de préstamos"
        agregar={
          puedeAgregar
            ? { etiqueta: "Agregar préstamo", onClick: () => setShowModal(true) }
            : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando préstamos…"
        filas={histoyPrestamos}
        columnas={columnas}
        detalleMovil={(row) => (
          <>
            {row.descripcion ? <div>{row.descripcion}</div> : null}
            <EstadoPrestamo cancelado={row.isCanceled} />
          </>
        )}
        filasPorPagina={FILAS_POR_PAGINA}
        etiquetaFila={(row) => `préstamo #${row.id}`}
        vacio={{
          titulo: "Sin préstamos",
          descripcion: "Aquí aparecen los préstamos del empleado y sus abonos.",
        }}
        acciones={(row) => [
          {
            id: "abonos",
            etiqueta: "Ver abonos",
            icono: <ListAltOutlinedIcon />,
            onClick: () => {
              setSelectedPrestamo(row);
              setShowDetalle(true);
            },
          },
          {
            id: "abonar",
            etiqueta: "Abonar",
            icono: <AddCardOutlinedIcon />,
            visible: puedeAbonar && !row.isCanceled,
            onClick: () => setAbonando(row),
          },
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            // Con abonos el servidor siempre lo rechaza: se anulan primero los abonos.
            visible: puedeEliminar && !(row.totalAbonado > 0),
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      {showModal ? (
        <AddPrestamo
          id={id}
          setShowModal={setShowModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {showDetalle && selectedPrestamo ? (
        <UpdatePrestamo
          selected={selectedPrestamo}
          setShowModal={setShowDetalle}
          onCambio={() => setLocalReload((actual) => !actual)}
          puedeAbonar={puedeAbonar}
          puedeAnular={puedeEliminar}
        />
      ) : null}

      {abonando ? (
        <AddAbono
          id={abonando.id}
          saldo={saldoDe(abonando)}
          setShowModal={(visible) => {
            if (!visible) setAbonando(null);
          }}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {dialogo}
    </div>
  );
};

export default Prestamos;
